import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { uploadFile } from '@api/fileImport.api';
import { addNotification } from '@redux/actions/notifications.actions';
import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import { ThreadAttachmentType } from '@ts/messageThread.types';

export const MAX_MESSAGE_ATTACHMENT_SIZE_MB = 25;
const BYTES_IN_MB = 1_000_000;

export type MessageAttachmentsHookType = {
  attachments: ThreadAttachmentType[];
  isUploading: boolean;
  uploadingProgress: number;
  handleAttachmentChange: (files: File[]) => Promise<void>;
  reset: () => void;
  /**
   * Increments on every reset. AttachFileZone keeps its file chips in local state,
   * so callers use this as its React `key` to remount it and clear the stale chip
   * after a message is sent.
   */
  resetToken: number;
};

/**
 * Uploads reply attachments to file storage and hands back the uid/name/type/size
 * descriptors the concern and change-request APIs expect.
 *
 * `resetKey` bumps whenever the surrounding surface reopens or switches records, so
 * an in-flight upload from a previous record can never land in the current one.
 */
export default function useMessageAttachments(resetKey: unknown): MessageAttachmentsHookType {
  const dispatch = useDispatch();
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const [attachments, setAttachments] = useState<ThreadAttachmentType[]>([]);
  const [uploadingProgress, setUploadingProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [resetToken, setResetToken] = useState(0);
  const uploadIdRef = useRef(0);

  const reset = () => {
    uploadIdRef.current += 1;
    setAttachments([]);
    setUploadingProgress(0);
    setIsUploading(false);
    setResetToken((prev) => prev + 1);
  };

  useEffect(() => {
    reset();
  }, [resetKey]);

  const uploadAttachment = async (file: File): Promise<ThreadAttachmentType> => {
    const buffer = await file.arrayBuffer();
    const processCallback = ([{ progress }]) => setUploadingProgress(progress);
    const processResponse = await uploadFile(undefined, workspaceId, file, buffer, processCallback);
    const { uid } = processResponse[processResponse.length - 1];

    return {
      uid,
      name: file.name,
      type: file.type,
      size: file.size,
    };
  };

  const handleAttachmentChange = async (files: File[]) => {
    // Validate BEFORE bumping uploadIdRef. Bumping first orphaned any in-flight
    // upload's `finally`, latching isUploading true and disabling Send for good.
    const oversizedFile = files.find(
      (file) => file.size > MAX_MESSAGE_ATTACHMENT_SIZE_MB * BYTES_IN_MB
    );

    if (oversizedFile) {
      dispatch(
        addNotification({
          title: `Maximum file size is ${MAX_MESSAGE_ATTACHMENT_SIZE_MB}MB`,
          type: 'warning',
        })
      );
      return;
    }

    uploadIdRef.current += 1;
    const uploadId = uploadIdRef.current;

    setAttachments([]);
    setUploadingProgress(0);

    if (!files.length) {
      setIsUploading(false);
      return;
    }

    setIsUploading(true);

    try {
      const uploaded = await Promise.all(files.map(uploadAttachment));

      if (uploadIdRef.current === uploadId) {
        setAttachments(uploaded);
      }
    } catch (err) {
      console.error(err);
      if (uploadIdRef.current === uploadId) {
        dispatch(addNotification({ title: 'File is not uploaded. Try again', type: 'error' }));
      }
    } finally {
      if (uploadIdRef.current === uploadId) {
        setIsUploading(false);
        setUploadingProgress(0);
      }
    }
  };

  return {
    attachments,
    isUploading,
    uploadingProgress,
    handleAttachmentChange,
    reset,
    resetToken,
  };
}
